import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

export default function AdminPayments() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [viewing, setViewing] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") {
      navigate("/login", { replace: true, state: { from: "/admin/payments" } });
      return;
    }
    if (user.role !== "admin") {
      toast.error("Admin only");
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/payments", { params: { status: statusFilter } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, user]);

  const verify = async (id, status) => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/${id}/verify`, { status, admin_note: adminNote });
      toast.success(status === "approved" ? "Approved & Premium activated" : "Rejected");
      setViewing(null);
      setAdminNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user || user.role !== "admin") {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container py-4" data-testid="admin-payments">
        <div className="d-flex justify-content-between align-items-end flex-wrap mb-4">
          <div>
            <div className="sb-kicker mb-1">Admin</div>
            <h1 className="h2 mb-0">Payments queue</h1>
          </div>
          <div className="btn-group" role="group">
            {["pending", "approved", "rejected", "all"].map((s) => (
              <button
                key={s}
                type="button"
                className={
                  "btn btn-sm " +
                  (statusFilter === s ? "btn-primary" : "btn-outline-primary")
                }
                onClick={() => setStatusFilter(s)}
                data-testid={`status-filter-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {items === null ? (
          <div className="sb-card">Loading…</div>
        ) : items.length === 0 ? (
          <div className="sb-card text-center py-5" data-testid="empty-payments">
            <div className="sb-kicker mb-2">Nothing here</div>
            <h3 className="h5">No {statusFilter} payments right now.</h3>
          </div>
        ) : (
          <div className="sb-card p-0 overflow-hidden">
            <div className="table-responsive">
              <table className="table align-middle mb-0" data-testid="payments-table">
                <thead className="text-muted small">
                  <tr>
                    <th className="ps-3">Member</th>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th>Reference</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th className="pe-3 text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                      <td className="ps-3">
                        <div className="fw-semibold">{p.user_name || "—"}</div>
                        <div className="small text-muted">{p.user_email}</div>
                      </td>
                      <td className="small">{p.plan}</td>
                      <td className="small">₹{p.amount}</td>
                      <td className="small text-muted">{p.upi_reference || "—"}</td>
                      <td className="small">{new Date(p.created_at).toLocaleString()}</td>
                      <td className="small text-capitalize">{p.status}</td>
                      <td className="pe-3 text-end">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setViewing(p)}
                          data-testid={`view-payment-${p.id}`}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {viewing && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(26,22,20,0.55)", zIndex: 1050 }}
          onClick={() => setViewing(null)}
          data-testid="review-modal"
        >
          <div
            className="sb-card mx-3"
            style={{ maxWidth: 620, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sb-kicker mb-2">Review payment</div>
            <h3 className="h4 mb-1">{viewing.user_name}</h3>
            <div className="text-muted small mb-3">{viewing.user_email}</div>
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <div className="small text-muted">Plan</div>
                <div>{viewing.plan}</div>
              </div>
              <div className="col-md-6">
                <div className="small text-muted">Amount</div>
                <div>₹{viewing.amount}</div>
              </div>
              <div className="col-md-6">
                <div className="small text-muted">UPI reference</div>
                <div>{viewing.upi_reference || "—"}</div>
              </div>
              <div className="col-md-6">
                <div className="small text-muted">Note</div>
                <div>{viewing.note || "—"}</div>
              </div>
            </div>

            {viewing.screenshot_url ? (
              <div className="mb-3">
                <div className="small text-muted mb-1">Screenshot</div>
                <img
                  src={viewing.screenshot_url}
                  alt="Payment screenshot"
                  style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid var(--sb-border)" }}
                  data-testid="review-screenshot"
                />
              </div>
            ) : (
              <div className="alert alert-warning py-2 small">No screenshot provided.</div>
            )}

            <label className="form-label">Admin note</label>
            <textarea
              rows={2}
              className="form-control mb-3"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Optional note visible to the user"
              data-testid="admin-note"
            />

            <div className="d-flex flex-wrap gap-2 justify-content-end">
              <button
                className="btn btn-outline-primary"
                onClick={() => setViewing(null)}
                data-testid="review-cancel"
              >
                Close
              </button>
              {viewing.status === "pending" && (
                <>
                  <button
                    className="btn btn-outline-danger rounded-pill"
                    disabled={busy}
                    onClick={() => verify(viewing.id, "rejected")}
                    data-testid="review-reject"
                  >
                    Reject
                  </button>
                  <button
                    className="btn btn-gold"
                    disabled={busy}
                    onClick={() => verify(viewing.id, "approved")}
                    data-testid="review-approve"
                  >
                    Approve & activate Premium
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
